import { useState, useCallback, useRef, type FormEvent, type ChangeEvent } from "react";
import { apiFetch } from "../../utils/api";
import { AppHeader } from "../../components/AppHeader";
import { AppFooter } from "../../components/AppFooter";
import { formatPrice, formatArea } from "../../utils/format";
import { useKakaoMap } from "./useKakaoMap";
import { BlindPreview } from "./BlindPreview";
import { useIsMobile } from "../../hooks/useIsMobile";
import { usePageTitle } from "../../hooks/usePageTitle";
import {
  PROPERTY_TYPES,
  INITIAL_FORM,
  type ListingForm,
  type ListingResponse,
} from "./types";

// ── 드롭다운 옵션 ────────────────────────
const LAND_CATEGORIES = [
  "전", "답", "임야", "대", "잡종지", "과수원", "공장용지", "창고용지", "기타",
];

const ZONING_OPTIONS = [
  "계획관리", "보전관리", "생산관리", "자연녹지", "농림지역", "기타",
];

const MAX_PHOTOS = 10;

// ── 사진 리사이즈 유틸 ────────────────────
function resizeImage(file: File, maxSize = 800, quality = 0.7): Promise<string> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(url);
      let w = img.width;
      let h = img.height;
      if (w > maxSize || h > maxSize) {
        if (w > h) {
          h = Math.round((h * maxSize) / w);
          w = maxSize;
        } else {
          w = Math.round((w * maxSize) / h);
          h = maxSize;
        }
      }
      const canvas = document.createElement("canvas");
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext("2d");
      if (!ctx) { reject(new Error("Canvas not supported")); return; }
      ctx.drawImage(img, 0, 0, w, h);
      resolve(canvas.toDataURL("image/jpeg", quality));
    };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error("Image load failed")); };
    img.src = url;
  });
}

export function CreateListingPage() {
  usePageTitle("\uB9E4\uBB3C \uB4F1\uB85D");
  const isMobile = useIsMobile();
  const [step, setStep] = useState<"form" | "success">("form");
  const [form, setForm] = useState<ListingForm>({ ...INITIAL_FORM });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<ListingResponse | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const fileInputRef = useRef<HTMLInputElement>(null);

  const update = useCallback(
    <K extends keyof ListingForm>(key: K, value: ListingForm[K]) =>
      setForm((prev) => ({ ...prev, [key]: value })),
    [],
  );

  // 핀 변경 콜백
  const handlePinChange = useCallback((lat: number, lng: number) => {
    setForm((prev) => ({
      ...prev,
      exact_lat: lat,
      exact_lng: lng,
    }));
  }, []);

  const { searchAddress, calcBlindCoords } = useKakaoMap({
    containerId: "listing-map",
    onPinChange: handlePinChange,
    level: 4,
  });

  // 블라인드 좌표 계산
  const handleCalcBlind = async () => {
    if (!form.region_sido || !form.region_sigungu) return;
    const coords = await calcBlindCoords(
      form.region_sido,
      form.region_sigungu,
      form.region_eupmyeondong,
    );
    if (coords) {
      setForm((prev) => ({
        ...prev,
        blind_lat: coords.lat,
        blind_lng: coords.lng,
      }));
    }
  };

  // ── 사진 처리 ──────────────────────────
  const handlePhotos = async (e: ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;
    const remaining = MAX_PHOTOS - form.photos.length;
    if (remaining <= 0) return;
    const toProcess = Array.from(files).slice(0, remaining);
    const newPhotos: string[] = [];
    for (const file of toProcess) {
      try {
        const base64 = await resizeImage(file);
        newPhotos.push(base64);
      } catch { /* skip failed image */ }
    }
    if (newPhotos.length > 0) {
      setForm((prev) => ({ ...prev, photos: [...prev.photos, ...newPhotos] }));
    }
    // reset input so same file can be selected again
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const removePhoto = (index: number) => {
    setForm((prev) => ({
      ...prev,
      photos: prev.photos.filter((_, i) => i !== index),
    }));
  };

  // 폼 제출
  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError("");

    // 유효성 검사
    if (!form.property_type) {
      setError("매물 유형을 선택해 주세요");
      return;
    }
    if (!form.region_sido || !form.region_sigungu || !form.region_eupmyeondong) {
      setError("소재지(시도/시군구/읍면동)를 모두 입력해 주세요");
      return;
    }
    if (!form.exact_address) {
      setError("정확한 지번 주소를 입력해 주세요");
      return;
    }
    if (!form.exact_lat || !form.exact_lng) {
      setError("지도에서 매물 위치를 클릭해 주세요");
      return;
    }
    const areaPyeong = parseFloat(form.area_pyeong);
    const priceManwon = parseFloat(form.price_manwon);
    if (!areaPyeong || areaPyeong <= 0) {
      setError("면적을 입력해 주세요");
      return;
    }
    if (!priceManwon || priceManwon <= 0) {
      setError("가격을 입력해 주세요");
      return;
    }

    // 블라인드 좌표가 없으면 자동 계산 시도
    let blindLat = form.blind_lat;
    let blindLng = form.blind_lng;
    if (!blindLat || !blindLng) {
      const coords = await calcBlindCoords(
        form.region_sido,
        form.region_sigungu,
        form.region_eupmyeondong,
      );
      if (coords) {
        blindLat = coords.lat;
        blindLng = coords.lng;
      } else {
        setError("블라인드 좌표 계산에 실패했습니다. 소재지를 확인해 주세요");
        return;
      }
    }

    setLoading(true);
    try {
      const res = await apiFetch<ListingResponse>("/api/listings", {
        method: "POST",
        body: JSON.stringify({
          property_type: form.property_type,
          region_sido: form.region_sido,
          region_sigungu: form.region_sigungu,
          region_eupmyeondong: form.region_eupmyeondong,
          exact_address: form.exact_address,
          area_pyeong: areaPyeong,
          price_manwon: priceManwon,
          exact_lat: form.exact_lat,
          exact_lng: form.exact_lng,
          blind_lat: blindLat,
          blind_lng: blindLng,
          land_category: form.land_category || undefined,
          zoning: form.zoning || undefined,
          road_access: form.road_access || undefined,
          water_supply: form.water_supply || undefined,
          recommended_use: form.recommended_use || undefined,
          description: form.description || undefined,
          detailed_notes: form.detailed_notes || undefined,
          owner_contact: form.owner_contact || undefined,
          photos: form.photos.length > 0 ? form.photos : undefined,
        }),
      });
      setResult(res);
      setStep("success");
    } catch (err: any) {
      setError(err.message || "매물 등록에 실패했습니다");
    } finally {
      setLoading(false);
    }
  };

  // 하나 더 등록
  const handleRegisterAnother = () => {
    setForm({ ...INITIAL_FORM });
    setResult(null);
    setSearchQuery("");
    setError("");
    setStep("form");
  };

  // iOS 줌 방지 fontSize (항상 16 — 모바일/데스크탑 모두)
  const inputFontSize = 16;

  // ── 성공 화면 ────────────────────────
  if (step === "success" && result) {
    return (
      <div style={styles.page}>
        <BlindPreview listing={result} onRegisterAnother={handleRegisterAnother} />
      </div>
    );
  }

  // ── 폼 화면 ──────────────────────────
  return (
    <div style={styles.page}>
      <AppHeader backTo="/dashboard" backLabel="← 뒤로" />
      <div style={{ ...styles.container, padding: isMobile ? "0 16px 48px" : "0 32px 48px" }}>

        <div style={{ ...styles.pageHeader, display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap" as const, gap: 12 }}>
          <div>
            <h1 style={styles.pageTitle}>블라인드 매물 등록</h1>
            <p style={styles.pageDesc}>매물 위치는 NDA 체결 후에만 공개됩니다</p>
          </div>
          <a
            href="/listings/bulk-upload"
            style={{
              display: "inline-flex", alignItems: "center", gap: 6,
              padding: "10px 18px", borderRadius: "var(--radius-md)",
              border: "1px solid var(--lt-gold)", background: "rgba(212,168,83,0.08)",
              color: "var(--lt-gold)", fontSize: 14, fontWeight: 600,
              textDecoration: "none", whiteSpace: "nowrap" as const,
              marginTop: 4,
            }}
          >
            {"📋"} 엑셀 일괄 등록
          </a>
        </div>

        <form onSubmit={handleSubmit} style={styles.form}>
          {/* Section 1: 매물 유형 */}
          <Section title="매물 유형">
            <SelectField
              label="유형 선택"
              value={form.property_type}
              onChange={(v) => update("property_type", v as ListingForm["property_type"])}
              options={PROPERTY_TYPES.map((t) => ({ value: t, label: t }))}
              placeholder="선택해 주세요"
              required
              fontSize={inputFontSize}
            />
          </Section>

          {/* Section 2: 소재지 */}
          <Section title="소재지">
            <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 14 }}>
              <Field
                label="시/도"
                value={form.region_sido}
                onChange={(v) => update("region_sido", v)}
                placeholder="예: 경기도"
                required
                fontSize={inputFontSize}
              />
              <Field
                label="시/군/구"
                value={form.region_sigungu}
                onChange={(v) => update("region_sigungu", v)}
                placeholder="예: 용인시 처인구"
                required
                fontSize={inputFontSize}
              />
            </div>
            <div style={fieldStyles.row}>
              <div style={{ flex: 1 }}>
                <Field
                  label="읍/면/동"
                  value={form.region_eupmyeondong}
                  onChange={(v) => update("region_eupmyeondong", v)}
                  placeholder="예: 포곡읍"
                  required
                  fontSize={inputFontSize}
                />
              </div>
              <button
                type="button"
                onClick={handleCalcBlind}
                style={styles.blindCalcBtn}
                title="블라인드 좌표 계산"
              >
                {"블라인드 좌표 계산"}
              </button>
            </div>
            {form.blind_lat && form.blind_lng && (
              <p style={styles.blindCoordText}>
                {"블라인드 좌표: "}
                {form.blind_lat}, {form.blind_lng}
              </p>
            )}
            <Field
              label="정확한 지번 주소"
              value={form.exact_address}
              onChange={(v) => update("exact_address", v)}
              placeholder="예: 경기도 용인시 처인구 포곡읍 삼계리 123"
              required
              fontSize={inputFontSize}
            />
          </Section>

          {/* Section 3: 위치 지도 */}
          <Section title="위치 지도">
            <div style={fieldStyles.row}>
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    searchAddress(searchQuery);
                  }
                }}
                placeholder="주소 또는 장소 검색"
                style={{ ...fieldStyles.input, flex: 1, fontSize: inputFontSize }}
              />
              <button
                type="button"
                onClick={() => searchAddress(searchQuery)}
                style={styles.searchBtn}
              >
                검색
              </button>
            </div>
            <div id="listing-map" style={styles.mapContainer} />
            {form.exact_lat && form.exact_lng ? (
              <p style={styles.coordText}>
                {"핀 좌표: "}
                {form.exact_lat.toFixed(6)}, {form.exact_lng.toFixed(6)}
              </p>
            ) : (
              <p style={styles.coordHint}>
                지도를 클릭하여 정확한 매물 위치를 지정해 주세요
              </p>
            )}
          </Section>

          {/* Section 4: 매물 정보 */}
          <Section title="매물 정보">
            <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 14 }}>
              <div>
                <Field
                  label="면적 (평)"
                  value={form.area_pyeong}
                  onChange={(v) => update("area_pyeong", v)}
                  placeholder="예: 500"
                  type="number"
                  required
                  fontSize={inputFontSize}
                />
                {form.area_pyeong && parseFloat(form.area_pyeong) > 0 && (
                  <p style={styles.previewText}>
                    {formatArea(parseFloat(form.area_pyeong))}
                  </p>
                )}
              </div>
              <div>
                <Field
                  label="가격 (만원)"
                  value={form.price_manwon}
                  onChange={(v) => update("price_manwon", v)}
                  placeholder="예: 50000 (5억)"
                  type="number"
                  required
                  fontSize={inputFontSize}
                />
                {form.price_manwon && parseFloat(form.price_manwon) > 0 && (
                  <p style={styles.previewText}>
                    {formatPrice(parseFloat(form.price_manwon))}원
                  </p>
                )}
              </div>
            </div>
            <SelectField
              label="지목"
              value={form.land_category}
              onChange={(v) => update("land_category", v)}
              options={LAND_CATEGORIES.map((c) => ({ value: c, label: c }))}
              placeholder="선택해 주세요"
              fontSize={inputFontSize}
            />
          </Section>

          {/* Section 5: 기타 정보 */}
          <Section title="기타 정보 (선택)">
            <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 14 }}>
              <SelectField
                label="용도지역"
                value={form.zoning}
                onChange={(v) => update("zoning", v)}
                options={ZONING_OPTIONS.map((z) => ({ value: z, label: z }))}
                placeholder="선택해 주세요"
                fontSize={inputFontSize}
              />
              <Field
                label="도로 접합"
                value={form.road_access}
                onChange={(v) => update("road_access", v)}
                placeholder="예: 4m 포장도로 접합"
                fontSize={inputFontSize}
              />
              <Field
                label="수도"
                value={form.water_supply}
                onChange={(v) => update("water_supply", v)}
                placeholder="예: 상수도, 관정(지하수)"
                fontSize={inputFontSize}
              />
              <Field
                label="추천 용도"
                value={form.recommended_use}
                onChange={(v) => update("recommended_use", v)}
                placeholder="예: 전원주택, 귀농, 캠핑장"
                fontSize={inputFontSize}
              />
            </div>
          </Section>

          {/* Section 6: 사진 */}
          <Section title={`사진 (${form.photos.length}/${MAX_PHOTOS}장)`}>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept="image/*"
              onChange={handlePhotos}
              style={{ display: "none" }}
            />
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={form.photos.length >= MAX_PHOTOS}
              style={{
                ...styles.photoAddBtn,
                opacity: form.photos.length >= MAX_PHOTOS ? 0.4 : 1,
              }}
            >
              {"+ 사진 추가"}
            </button>
            {form.photos.length > 0 && (
              <div style={{
                display: "grid",
                gridTemplateColumns: isMobile ? "repeat(3, 1fr)" : "repeat(4, 1fr)",
                gap: 8,
              }}>
                {form.photos.map((src, i) => (
                  <div key={i} style={styles.thumbWrap}>
                    <img src={src} alt={`사진 ${i + 1}`} style={styles.thumbImg} />
                    <button
                      type="button"
                      onClick={() => removePhoto(i)}
                      style={styles.thumbRemove}
                    >
                      X
                    </button>
                  </div>
                ))}
              </div>
            )}
            <p style={styles.coordHint}>
              JPEG로 변환, 최대 800px 리사이즈 (최대 {MAX_PHOTOS}장)
            </p>
          </Section>

          {/* Section 7: 메모 */}
          <Section title="메모">
            <TextareaField
              label="공개 메모"
              value={form.description}
              onChange={(v) => update("description", v)}
              placeholder="다른 중개사에게 보이는 메모 (매물 특징, 장점 등)"
              rows={3}
              fontSize={inputFontSize}
            />
            <TextareaField
              label="비공개 메모 (NDA 후 공개)"
              value={form.detailed_notes}
              onChange={(v) => update("detailed_notes", v)}
              placeholder="NDA 체결 후에만 공개되는 상세 정보"
              rows={3}
              fontSize={inputFontSize}
            />
            <Field
              label="소유주 연락처 (NDA 후 공개)"
              value={form.owner_contact}
              onChange={(v) => update("owner_contact", v)}
              placeholder="010-0000-0000"
              fontSize={inputFontSize}
            />
          </Section>

          {/* 에러 메시지 */}
          {error && <p style={styles.error}>{error}</p>}

          {/* 등록 버튼 */}
          <button
            type="submit"
            disabled={loading}
            style={{
              ...styles.submitBtn,
              opacity: loading ? 0.6 : 1,
            }}
          >
            {loading ? "등록 중..." : "블라인드 매물 등록"}
          </button>
        </form>
      </div>
      <AppFooter />
    </div>
  );
}

// ── 내부 컴포넌트 ────────────────────────

function Section({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div style={styles.section}>
      <h3 style={styles.sectionTitle}>{title}</h3>
      <div style={styles.sectionBody}>{children}</div>
    </div>
  );
}

function Field({
  label,
  value,
  onChange,
  placeholder,
  type = "text",
  required = false,
  fontSize = 14,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  type?: string;
  required?: boolean;
  fontSize?: number;
}) {
  return (
    <div style={fieldStyles.field}>
      <label style={fieldStyles.label}>
        {label}
        {required && <span style={fieldStyles.required}> *</span>}
      </label>
      <input
        type={type}
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        style={{ ...fieldStyles.input, fontSize }}
        required={required}
      />
    </div>
  );
}

function SelectField({
  label,
  value,
  onChange,
  options,
  placeholder,
  required = false,
  fontSize = 14,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: { value: string; label: string }[];
  placeholder?: string;
  required?: boolean;
  fontSize?: number;
}) {
  return (
    <div style={fieldStyles.field}>
      <label style={fieldStyles.label}>
        {label}
        {required && <span style={fieldStyles.required}> *</span>}
      </label>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        style={{ ...fieldStyles.input, fontSize }}
        required={required}
      >
        <option value="">{placeholder || "선택"}</option>
        {options.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>
    </div>
  );
}

function TextareaField({
  label,
  value,
  onChange,
  placeholder,
  rows = 3,
  fontSize = 14,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  rows?: number;
  fontSize?: number;
}) {
  return (
    <div style={fieldStyles.field}>
      <label style={fieldStyles.label}>{label}</label>
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        rows={rows}
        style={{
          ...fieldStyles.input,
          fontSize,
          resize: "vertical" as const,
          fontFamily: "inherit",
          lineHeight: 1.5,
        }}
      />
    </div>
  );
}

// ── 스타일 ──────────────────────────

const styles: Record<string, React.CSSProperties> = {
  page: {
    minHeight: "100vh",
    background: "var(--bg-primary)",
  },
  container: {
    maxWidth: 1200,
    margin: "0 auto",
    padding: "0 16px 40px",
  },
  pageHeader: {
    padding: "28px 0 4px",
  },
  pageTitle: {
    fontSize: 22,
    fontWeight: 800,
    color: "var(--text-primary)",
    margin: 0,
    letterSpacing: "-0.3px",
  },
  pageDesc: {
    fontSize: 13,
    color: "var(--text-muted)",
    margin: "6px 0 24px",
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "16px 0",
    borderBottom: "1px solid var(--border-light)",
    marginBottom: 20,
  },
  backBtn: {
    color: "var(--text-muted)",
    textDecoration: "none",
    fontSize: 14,
    fontWeight: 500,
    padding: "8px 0",
  },
  title: {
    fontSize: 18,
    fontWeight: 700,
    color: "var(--text-primary)",
    margin: 0,
  },
  form: {
    display: "flex",
    flexDirection: "column" as const,
    gap: 24,
  },
  section: {
    background: "var(--bg-card)",
    border: "1px solid var(--border-light)",
    borderLeft: "3px solid var(--lt-gold)",
    borderRadius: "var(--radius-xl)",
    overflow: "hidden",
    boxShadow: "var(--shadow-sm)",
  },
  sectionTitle: {
    fontSize: 13,
    fontWeight: 700,
    color: "var(--lt-gold)",
    margin: 0,
    padding: "12px 20px",
    borderBottom: "1px solid var(--border-light)",
    letterSpacing: "0.8px",
    textTransform: "uppercase" as const,
    background: "rgba(212,168,83,0.04)",
  },
  sectionBody: {
    padding: 20,
    display: "flex",
    flexDirection: "column" as const,
    gap: 14,
  },
  mapContainer: {
    width: "100%",
    height: 280,
    borderRadius: "var(--radius-lg)",
    border: "1px solid var(--border-light)",
    background: "var(--bg-secondary)",
  },
  searchBtn: {
    padding: "10px 16px",
    borderRadius: "var(--radius-md)",
    border: "none",
    background: "var(--lt-gold)",
    color: "#0F172A",
    fontSize: 14,
    fontWeight: 600,
    cursor: "pointer",
    whiteSpace: "nowrap" as const,
  },
  blindCalcBtn: {
    padding: "10px 12px",
    borderRadius: "var(--radius-md)",
    border: "1px solid var(--lt-gold)",
    background: "transparent",
    color: "var(--lt-gold)",
    fontSize: 12,
    fontWeight: 600,
    cursor: "pointer",
    whiteSpace: "nowrap" as const,
    alignSelf: "flex-end",
    marginTop: 22,
  },
  coordText: {
    fontSize: 12,
    color: "var(--lt-green)",
    margin: 0,
  },
  coordHint: {
    fontSize: 12,
    color: "var(--text-muted)",
    margin: 0,
  },
  blindCoordText: {
    fontSize: 12,
    color: "var(--lt-green)",
    margin: 0,
    padding: "4px 8px",
    background: "rgba(16, 185, 129, 0.1)",
    borderRadius: "var(--radius-sm)",
  },
  previewText: {
    fontSize: 13,
    color: "var(--lt-gold)",
    margin: "4px 0 0",
    fontWeight: 500,
  },
  error: {
    color: "var(--lt-red)",
    fontSize: 14,
    margin: 0,
    padding: "10px 16px",
    background: "rgba(239, 68, 68, 0.1)",
    borderRadius: "var(--radius-md)",
  },
  submitBtn: {
    padding: "16px",
    borderRadius: "var(--radius-md)",
    border: "none",
    background: "linear-gradient(135deg, #B8972E, #D4AF37)",
    color: "#0F172A",
    fontSize: 16,
    fontWeight: 700,
    cursor: "pointer",
    transition: "var(--transition-fast)",
    letterSpacing: "0.3px",
  },
  photoAddBtn: {
    padding: "12px",
    borderRadius: "var(--radius-md)",
    border: "2px dashed var(--border-medium)",
    background: "transparent",
    color: "var(--text-secondary)",
    fontSize: 14,
    fontWeight: 600,
    cursor: "pointer",
    width: "100%",
  },
  thumbWrap: {
    position: "relative" as const,
    aspectRatio: "1",
    borderRadius: "var(--radius-md)",
    overflow: "hidden",
    border: "1px solid var(--border-light)",
  },
  thumbImg: {
    width: "100%",
    height: "100%",
    objectFit: "cover" as const,
    display: "block",
  },
  thumbRemove: {
    position: "absolute" as const,
    top: 4,
    right: 4,
    width: 24,
    height: 24,
    borderRadius: "50%",
    border: "none",
    background: "rgba(0,0,0,0.6)",
    color: "#fff",
    fontSize: 12,
    fontWeight: 700,
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 0,
    lineHeight: 1,
  },
};

const fieldStyles: Record<string, React.CSSProperties> = {
  field: {
    display: "flex",
    flexDirection: "column" as const,
    gap: 6,
  },
  label: {
    fontSize: 14,
    fontWeight: 500,
    color: "var(--text-secondary)",
  },
  required: {
    color: "var(--lt-red)",
  },
  input: {
    padding: "12px 14px",
    borderRadius: "var(--radius-md)",
    border: "1px solid var(--border-medium)",
    background: "var(--bg-secondary)",
    color: "var(--text-primary)",
    fontSize: 16,
    outline: "none",
    transition: "var(--transition-fast)",
    width: "100%",
    boxSizing: "border-box" as const,
  },
  row: {
    display: "flex",
    gap: 10,
    alignItems: "flex-start",
  },
};
